import type { ParseKeys } from 'i18next';

import { expandGridAreaSpec } from '@/utils/gridSpec';

import {
  LayoutTemplate,
  LayoutTemplatesByGroup,
  ScreenRatio,
  TemplateGroup,
  TemplateWidget,
} from './layoutTemplates.types.ts';
import { standardTemplates } from './layoutTemplates/standard.ts';
import { ultrawideTemplates } from './layoutTemplates/ultrawide.ts';
import { videoTemplates } from './layoutTemplates/video.ts';
import { wideTemplates } from './layoutTemplates/wide.ts';

const SCREEN_RATIO_ASPECTS: { ratio: ScreenRatio; aspect: number }[] = [
  { ratio: ScreenRatio.Ratio_16_9, aspect: 16 / 9 },
  { ratio: ScreenRatio.Ratio_16_10, aspect: 16 / 10 },
  { ratio: ScreenRatio.Ratio_24_10, aspect: 24 / 10 },
  { ratio: ScreenRatio.Ratio_32_9, aspect: 32 / 9 },
];

/** Relative tolerance when matching physical screen dimensions to a known ratio. */
const ASPECT_RATIO_MATCH_TOLERANCE = 0.03;

export const screenDimensionsToScreenRatio = (
  screenWidth: number,
  screenHeight: number
): ScreenRatio => {
  if (screenWidth <= 0 || screenHeight <= 0) {
    return ScreenRatio.Other;
  }

  const aspect = screenWidth / screenHeight;

  let closestRatio = ScreenRatio.Other;
  let smallestRelativeDiff = Infinity;

  for (const { ratio, aspect: targetAspect } of SCREEN_RATIO_ASPECTS) {
    const relativeDiff = Math.abs(aspect - targetAspect) / targetAspect;
    if (relativeDiff < smallestRelativeDiff) {
      smallestRelativeDiff = relativeDiff;
      closestRatio = ratio;
    }
  }

  return smallestRelativeDiff <= ASPECT_RATIO_MATCH_TOLERANCE ? closestRatio : ScreenRatio.Other;
};

/** Which template group best matches a detected screen ratio (drives the default/highlighted tab). */
export const screenRatioToTemplateGroup = (screenRatio: ScreenRatio): TemplateGroup => {
  switch (screenRatio) {
    case ScreenRatio.Ratio_24_10:
      return TemplateGroup.Wide;
    case ScreenRatio.Ratio_32_9:
      return TemplateGroup.Ultrawide;
    default:
      return TemplateGroup.Standard;
  }
};

/**
 * Preview extent (fine columns × rows) per group, used to expand a spec for the template icon.
 * Chosen so it divides evenly by every column and row count used by that group's templates —
 * otherwise equal cells round unevenly in the icon (e.g. 3 columns over a width of 32 → 11/11/10).
 * Standard uses 120 × 90 (4:3) so it divides by 2/3/4/5 columns and 2/3 rows. On a real screen
 * this never matters because grid widths are multiples of the column subdivision.
 */
const GROUP_PREVIEW_EXTENT: Record<TemplateGroup, { totalColumns: number; totalRows: number }> = {
  [TemplateGroup.Standard]: { totalColumns: 120, totalRows: 90 },
  [TemplateGroup.Wide]: { totalColumns: 72, totalRows: 40 },
  [TemplateGroup.Ultrawide]: { totalColumns: 96, totalRows: 36 },
  [TemplateGroup.Video]: { totalColumns: 48, totalRows: 36 },
};

/** Expands a template's spec into preview widgets sized to its group aspect (for the icon). */
export const getTemplatePreviewWidgets = (template: LayoutTemplate): TemplateWidget[] =>
  expandGridAreaSpec(template.spec, GROUP_PREVIEW_EXTENT[template.group]);

/**
 * Hardcoded layout templates. Each is a resolution-independent {@link LayoutTemplate}; its spec
 * is expanded to fill the current screen when a layout is created from it.
 */
export const LAYOUT_TEMPLATES: LayoutTemplate[] = [
  ...standardTemplates,
  ...wideTemplates,
  ...ultrawideTemplates,
  ...videoTemplates,
];

const createEmptyLayoutTemplatesByGroup = (): LayoutTemplatesByGroup => ({
  [TemplateGroup.Standard]: [],
  [TemplateGroup.Wide]: [],
  [TemplateGroup.Ultrawide]: [],
  [TemplateGroup.Video]: [],
});

export const getGroupedLayoutTemplates = (): LayoutTemplatesByGroup => {
  const grouped = createEmptyLayoutTemplatesByGroup();
  for (const template of LAYOUT_TEMPLATES) {
    grouped[template.group].push(template);
  }

  for (const group of Object.values(TemplateGroup)) {
    grouped[group].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    );
  }

  return grouped;
};

export type LayoutTemplateId = (typeof LAYOUT_TEMPLATES)[number]['id'];

export const layoutTemplateNameKey = (
  templateId: LayoutTemplateId | string
): ParseKeys<'landingPage'> => `templates.id.${templateId}` as ParseKeys<'landingPage'>;
